import { Button, Form, Popover, Switch } from 'antd';
import { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import StatusButton from '../../../../../../../components/statusButton';
import ProductStatusTabs, { TProductStatus } from '../../../../../../../components/ProductStatusTabs/ProductStatusTabs';
import { ICountryExchangeDetail, IProviderDetails } from '../../../../../../../types/countries';
import { IProductDetailId, IProductUpdateBlockedByQuotes, PRODUCT_STATUS } from 'types/products';
import PricingInputField from '../PricingInputField/PricingInputField';
import { NudosMaskedNumberInput } from '../../../../../../../components/NudosComponents';
import { roundUpNumber } from '../../../../../../../utils/formatNumbers';
import useOnlyDevelopers from '../../../../../../../state/setOnlyDevelopers';
import NudosDropdownWithSearchbar from '../../../../../../../components/NudosComponents/NudosDropdownWithSearchbar/NudosDropdownWithSearchbar';
import BordModalConfirm from 'components/NudosComponents/BordModalConfirm/BordModalConfirm';
import { UpdateBlockedByQuotesModal } from '../../../../../components';
import { updateProductCountryStoreStatus } from '../../../../../../../services/cataloge';
import { displayErrorNotification, displaySuccessNotification } from 'utils/displayNudosStandardNotifications';
import { IPendingStoreStatusChange } from '../../../../FormProduct';
import './PricingDataByCountry.scss';

interface IpricingDataByCountryProps {
  currencyCode: string;
  flag?: string;
  countryName: string;
  countryNameCode: string;
  preloadedExchangeRate?: number;
  preloadedTaxRate?: number;
  preloadedStatus: string;
  preloadedProviderPrice?: number | null;
  preloadedMargin?: number | null;
  preloadedNudosPriceUsdWithTaxes?: number | null;
  preloadedNudosPriceUsd?: number | null;
  id?: number;
  productId?: number;
  setVerifyPricing?: (areVerified: boolean) => void;
  setChangePricing?: (change: boolean) => void;
  setProductDataDetail?: (data: IProductDetailId) => void;
  countriesOpenForSale?: ICountryExchangeDetail[];
  availableProviders?: IProviderDetails[];
  providerId?: number | null;
  pricingisActive?: boolean;
  isQuoteProduct?: boolean;
  addPendingStoreStatusChange?: (change: IPendingStoreStatusChange) => void;
}

const PricingDataByCountry = (props: IpricingDataByCountryProps) => {
  const {
    currencyCode,
    flag,
    countryName,
    countryNameCode,
    preloadedExchangeRate,
    preloadedTaxRate,
    pricingisActive,
    preloadedStatus,
    preloadedProviderPrice,
    preloadedMargin,
    preloadedNudosPriceUsdWithTaxes,
    preloadedNudosPriceUsd,
    id,
    productId,
    setVerifyPricing,
    setChangePricing,
    setProductDataDetail,
    availableProviders,
    providerId,
    isQuoteProduct,
    addPendingStoreStatusChange
  } = props;
  const { isOnlyDevsAutomates } = useOnlyDevelopers();
  const [activeCheck, setActiveCheck] = useState<boolean>(false);
  const [showActiveStatusPopover, setShowActiveStatusPopover] = useState(false);
  const [showPricingModalConfirm, setShowPricingModalConfirm] = useState(false);
  const [productStatus, setProductStatus] = useState<TProductStatus>(PRODUCT_STATUS.OFF);
  const [previousStatus, setPreviousStatus] = useState<TProductStatus>(PRODUCT_STATUS.OFF);
  const [pendingNewStatus, setPendingNewStatus] = useState<TProductStatus | null>(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);
  const [blockedByQuotesError, setBlockedByQuotesError] = useState<{
    errorData: IProductUpdateBlockedByQuotes;
    countryNames: string[];
  }>();
  const { setFieldsValue } = Form.useFormInstance();
  const providerPrice = Form.useWatch(`${countryNameCode}-providerPrice`);
  const margin = Form.useWatch(`${countryNameCode}-margin`);
  const exchangeRate = Form.useWatch(`${countryNameCode}-exchangeRate`);
  const taxRate = Form.useWatch(`${countryNameCode}-taxRate`);
  const nudosPriceUsd = Form.useWatch(`${countryNameCode}-nudosPriceUsd`);
  const nudosPriceUsdWithTaxes = Form.useWatch(`${countryNameCode}-nudosPriceUsdWithTaxes`);
  const nudosProviderId = Form.useWatch(`${countryNameCode}-providerId`);
  const [newProvider, setNewProvider] = useState<IProviderDetails>();
  const [highlightMissingFields, setHighlightMissingFields] = useState(false);
  const isMarginValid = !margin || Math.abs(margin) < 100;
  const isProviderPriceValid = !providerPrice || +providerPrice > 0;
  const isNudosPriceUsdWithTaxesInputValid = !nudosPriceUsdWithTaxes || +nudosPriceUsdWithTaxes > 0;
  const negativeOrZeroMarginAlert = !isMarginValid || (margin <= 0 && nudosPriceUsdWithTaxes) ? ' ' : undefined;
  const productHasFullPricingDataForCountry =
    !!providerPrice && !!margin && !!exchangeRate && !!taxRate && !!nudosPriceUsdWithTaxes;
  const hasUnsavedPricingChanges =
    providerPrice !== preloadedProviderPrice ||
    margin !== preloadedMargin ||
    nudosPriceUsdWithTaxes !== preloadedNudosPriceUsdWithTaxes ||
    nudosProviderId !== providerId;
  const needsSaveBeforeTurnOn = !!productId && !!id && hasUnsavedPricingChanges;
  const emptyFieldPlaceholder = '$0 USD';

  /**
   * Devuelve el texto de error para destacar un campo requerido vacío cuando el
   * usuario intenta pasar a "Prendido" sin completar los datos. El espacio en blanco
   * activa los estilos de error sin mostrar mensaje, siguiendo el patrón de los inputs.
   */
  const requiredFieldError = (isEmpty: boolean) => (highlightMissingFields && isEmpty ? ' ' : undefined);

  const pricingFields = [
    {
      componentSize: 'extraSmall',
      dataName: `${countryNameCode}-taxRate`,
      label: 'Impuestos',
      initialValue: preloadedTaxRate,
      currentValue: taxRate,
      suffixSymbol: '%',
      placeholder: '%',
      isRequired: false,
      isDisabled: true
    },
    {
      componentSize: 'extraSmall',
      dataName: `${countryNameCode}-exchangeRate`,
      label: 'Tasa de cambio',
      initialValue: preloadedExchangeRate,
      currentValue: exchangeRate,
      suffixSymbol: currencyCode,
      placeholder: `${currencyCode}/USD`,
      isRequired: false,
      isDisabled: true
    }
  ];

  const handleStatusChangeMessage = (newStatus: TProductStatus): string => {
    const statusMessages: Partial<Record<TProductStatus, string>> = {
      [PRODUCT_STATUS.ON]: 'El producto será visible en Tienda para comprar',
      [PRODUCT_STATUS.TO_QUOTE]: 'El producto seguirá siendo visible para el cliente, pero no mostrará el precio.',
      [PRODUCT_STATUS.OFF]: 'Se ocultará el producto en la tienda'
    };
    return statusMessages[newStatus] || '';
  };

  const handleLabelConfirmButton = (newStatus: TProductStatus): string => {
    const buttonLabels: Partial<Record<TProductStatus, string>> = {
      [PRODUCT_STATUS.ON]: 'Sí, prender en tienda',
      [PRODUCT_STATUS.OFF]: 'Sí, apagar país ',
      [PRODUCT_STATUS.TO_QUOTE]: 'Sí, marcar para cotizar '
    };
    return buttonLabels[newStatus] || '';
  };

  const handleMessageSuccess = (newStatus: TProductStatus): string => {
    const statusMessages: Partial<Record<TProductStatus, string>> = {
      [PRODUCT_STATUS.ON]: 'País prendido en tienda con éxito',
      [PRODUCT_STATUS.TO_QUOTE]: 'País marcado para cotizar con éxito.',
      [PRODUCT_STATUS.OFF]: 'País apagado con éxito'
    };
    return statusMessages[newStatus] || '';
  };

  const handleStatusChange = (newStatus: TProductStatus, previousStatus: TProductStatus) => {
    const requiresFullData = newStatus === PRODUCT_STATUS.ON;

    if (requiresFullData && !productHasFullPricingDataForCountry) {
      setHighlightMissingFields(true);
      return;
    }

    if (requiresFullData && needsSaveBeforeTurnOn) {
      displayErrorNotification({
        customJSXMessage: <>Primero guarda los precios para poder prender el país en tienda</>
      });
      return;
    }

    setPendingNewStatus(newStatus);
    setPreviousStatus(previousStatus);
    setShowPricingModalConfirm(true);
  };

  const handleChangeStatus = (newStatus: TProductStatus) => {
    const statusConfig: Partial<Record<TProductStatus, { isActive: boolean; isQuoteProduct: boolean }>> = {
      [PRODUCT_STATUS.ON]: { isActive: true, isQuoteProduct: false },
      [PRODUCT_STATUS.OFF]: { isActive: false, isQuoteProduct: false },
      [PRODUCT_STATUS.TO_QUOTE]: { isActive: false, isQuoteProduct: true }
    };

    const config = statusConfig[newStatus];
    if (config) {
      setFieldsValue({
        [`${countryNameCode}-isActive`]: config.isActive,
        [`${countryNameCode}-isQuoteProduct`]: config.isQuoteProduct
      });
    }
  };

  /**
   * Confirma el cambio de estado en tienda del país.
   * - En creación (sin productId): actualiza el form local; el estado viajará con el POST de createProduct.
   * - País agregado en sesión y aún sin persistir (con productId pero sin id): registra el cambio en
   *   pendingStoreStatusChanges para aplicarlo vía PATCH tras el PUT del Guardar (el PATCH inmediato no
   *   aplica porque el país todavía no existe en backend). Evita que un pendiente viejo revierta el estado.
   * - En edición de país ya persistido (con productId e id): dispara PATCH al endpoint dedicado y refresca
   *   el detalle con la respuesta. Maneja el error 400 con affectedQuoteIds mostrando UpdateBlockedByQuotesModal.
   * @param ignoreActiveQuotes Si es true, fuerza el cambio aun con cotizaciones activas (solo al prender).
   */
  const handleConfirmStatusChange = async (ignoreActiveQuotes = false) => {
    if (pendingNewStatus === null) return;

    const statusToBody: Partial<Record<TProductStatus, { isActive: boolean; isQuoteProduct: boolean }>> = {
      [PRODUCT_STATUS.ON]: { isActive: true, isQuoteProduct: false },
      [PRODUCT_STATUS.OFF]: { isActive: false, isQuoteProduct: false },
      [PRODUCT_STATUS.TO_QUOTE]: { isActive: false, isQuoteProduct: true }
    };
    const config = statusToBody[pendingNewStatus];
    if (!config) return;

    if (!productId) {
      setProductStatus(pendingNewStatus);
      handleChangeStatus(pendingNewStatus);
      setChangePricing && setChangePricing(true);
      displaySuccessNotification({ customJSXMessage: <>{handleMessageSuccess(pendingNewStatus)}</> });
      setPendingNewStatus(null);
      setShowPricingModalConfirm(false);
      return;
    }

    if (!id) {
      setProductStatus(pendingNewStatus);
      handleChangeStatus(pendingNewStatus);
      addPendingStoreStatusChange?.({
        countryCode: countryNameCode,
        isActive: config.isActive,
        isQuoteProduct: config.isQuoteProduct
      });
      setChangePricing && setChangePricing(true);
      displaySuccessNotification({ customJSXMessage: <>{handleMessageSuccess(pendingNewStatus)}</> });
      setPendingNewStatus(null);
      setShowPricingModalConfirm(false);
      return;
    }

    setIsUpdatingStatus(true);
    try {
      const updatedProduct = await updateProductCountryStoreStatus(productId, {
        countryCode: countryNameCode,
        ...config,
        ...(ignoreActiveQuotes ? { ignoreActiveQuotes: true } : {})
      });
      setProductStatus(pendingNewStatus);
      setProductDataDetail && setProductDataDetail(updatedProduct);
      displaySuccessNotification({ customJSXMessage: <>{handleMessageSuccess(pendingNewStatus)}</> });
      setBlockedByQuotesError(undefined);
      setPendingNewStatus(null);
      setShowPricingModalConfirm(false);
    } catch (error) {
      const err = error as AxiosError;
      const data = err?.response?.data;
      if (err?.response?.status === 400 && Array.isArray(data?.affectedQuoteIds)) {
        setBlockedByQuotesError({
          errorData: data as IProductUpdateBlockedByQuotes,
          countryNames: [countryName]
        });
        setShowPricingModalConfirm(false);
      } else {
        if (err?.response?.status === 400 && data?.message) {
          displayErrorNotification({ customJSXMessage: <>{data.message}</> });
        } else {
          displayErrorNotification();
        }
        setBlockedByQuotesError(undefined);
        setPendingNewStatus(null);
        setShowPricingModalConfirm(false);
      }
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleCancelStatusChange = () => {
    setPendingNewStatus(null);
    setShowPricingModalConfirm(false);
  };


  const handleCloseBlockedByQuotes = () => {
    setBlockedByQuotesError(undefined);
    setPendingNewStatus(null);
  };

 
  
  const handleForceStatusChange = () => {
    handleConfirmStatusChange(true);
  };


  const handleForceActionVerb = (status: TProductStatus): string => {
    const verbs: Partial<Record<TProductStatus, string>> = {
      [PRODUCT_STATUS.ON]: 'prender',
      [PRODUCT_STATUS.OFF]: 'apagar',
      [PRODUCT_STATUS.TO_QUOTE]: 'marcar para cotizar'
    };
    return verbs[status] || '';
  };

 
  const handleForceButtonLabel = (status: TProductStatus): string => {
    const labels: Partial<Record<TProductStatus, string>> = {
      [PRODUCT_STATUS.ON]: 'Prender de todas formas',
      [PRODUCT_STATUS.OFF]: 'Apagar de todas formas',
      [PRODUCT_STATUS.TO_QUOTE]: 'Marcar de todas formas'
    };
    return labels[status] || '';
  };

  const handleClickSwitchActiveStatus = () => {
    if (!productHasFullPricingDataForCountry) {
      setHighlightMissingFields(true);
      return;
    }
    setShowActiveStatusPopover(true);
  };

  const changeActiveStatusSwith = () => {
    setActiveCheck(!activeCheck);
    setChangePricing && setChangePricing(true);
    setShowActiveStatusPopover(false);
  };

  const confirmChangeActiveStatusPopoverContent = (
    <div className="confirmChangeActiveStatusPopoverContent">
      <div className="textPopup">¿Seguro quieres {activeCheck ? 'apagar' : 'prender'} la herramienta en este país?</div>
      <Button className="buttonPoup redButton" onClick={changeActiveStatusSwith}>
        Si
      </Button>
      <Button className="buttonPoup" onClick={() => setShowActiveStatusPopover(false)}>
        No
      </Button>
    </div>
  );

  const getEcommercePriceSelectLabel = (includesTax: boolean) => {
    if (includesTax) {
      return <label className="ecommercePriceLabel labelSize">Precio con impuestos</label>;
    }
    return <label className="ecommercePriceLabel labelSize">Precio sin impues.</label>;
  };

  const calculateMarginProportion = (marginParam?: number) => {
    if (typeof marginParam !== 'number' || Math.abs(marginParam) >= 100) return;
    const getMarginProportion = marginParam / 100;
    const calculatedMarginFactor = 1 - getMarginProportion;
    return calculatedMarginFactor;
  };

  const calculateSellingPriceField = (margin: number, includesTax: boolean) => {
    const calculatedMargin = calculateMarginProportion(margin);
    const buyingTaxFactor = +taxRate / 100 + 1;
    const subtotal = calculatedMargin ? +providerPrice / calculatedMargin : 0;
    if (isNaN(subtotal)) return 0;
    const usdTotalPreTax = +(subtotal / exchangeRate);
    const usdTotalPlusTax = +(subtotal / exchangeRate) * buyingTaxFactor;
    if (!includesTax) return usdTotalPreTax;
    if (includesTax) return usdTotalPlusTax;
  };

  const calculateMarginField = (nudosPriceUsdWithTaxes: number) => {
    // This formula is obtained solving for the margin in the one used to obtain the selling price
    const taxFactor = +taxRate ? 1 + taxRate / 100 : 1;
    const providerPriceTimesTaxes = +providerPrice ? providerPrice * taxFactor : 0;
    const exchangeTimesTotal = +exchangeRate && +nudosPriceUsdWithTaxes ? exchangeRate * nudosPriceUsdWithTaxes : 0;
    const providerPriceToSellingPriceProportion = exchangeTimesTotal ? providerPriceTimesTaxes / exchangeTimesTotal : 1;
    const calculatedMargin = 1 - providerPriceToSellingPriceProportion;
    return calculatedMargin * 100;
  };

  const handleChangeMargin = (newMarginInput: string) => {
    setFieldsValue({
      [`${countryNameCode}-margin`]: newMarginInput || 0
    });
    if (newMarginInput?.length < 1) {
      setFieldsValue({
        [`${countryNameCode}-nudosPrice`]: 0
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPriceUsd`]: 0
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPriceWithTaxes`]: 0
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPriceUsdWithTaxes`]: 0
      });
      return;
    }
    const newMargin = isNaN(+newMarginInput) ? 0 : +newMarginInput;
    const newSellingPricePreTax = calculateSellingPriceField(newMargin, false);
    const newSellingPriceAfterTax = calculateSellingPriceField(newMargin, true);
    setFieldsValue({
      [`${countryNameCode}-nudosPriceUsd`]: newSellingPricePreTax ? `${roundUpNumber(newSellingPricePreTax, 2)}` : 0
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPrice`]: newSellingPricePreTax
        ? `${roundUpNumber(newSellingPricePreTax * +exchangeRate, 2)}`
        : 0
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPriceUsdWithTaxes`]: newSellingPriceAfterTax
        ? `${roundUpNumber(newSellingPriceAfterTax, 2)}`
        : 0
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPriceWithTaxes`]: newSellingPriceAfterTax
        ? `${roundUpNumber(newSellingPriceAfterTax * +exchangeRate, 2)}`
        : 0
    });
  };

  const handleChangeNudosPriceUsdWithTaxes = (nudosPriceUsdWithTaxesInput: string) => {
    setFieldsValue({
      [`${countryNameCode}-nudosPriceUsdWithTaxes`]: nudosPriceUsdWithTaxesInput
    });
    if (nudosPriceUsdWithTaxesInput?.length < 1) {
      setFieldsValue({
        [`${countryNameCode}-nudosPriceWithTaxes`]: 0
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPriceUsd`]: 0
      });
      setFieldsValue({
        [`${countryNameCode}-nudosPrice`]: 0
      });
      setFieldsValue({
        [`${countryNameCode}-margin`]: 0
      });
      return;
    }
    const sellingPriceNumber = nudosPriceUsdWithTaxesInput ? +nudosPriceUsdWithTaxesInput : 0;
    const calculatedMargin = calculateMarginField(sellingPriceNumber);
    const taxFactor = +taxRate ? 1 + taxRate / 100 : 1;
    const newPricePreTax = sellingPriceNumber ? sellingPriceNumber / taxFactor : 0;
    setFieldsValue({
      [`${countryNameCode}-nudosPriceWithTaxes`]: sellingPriceNumber
        ? `${roundUpNumber(sellingPriceNumber * +exchangeRate, 2)}`
        : 0
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPriceUsd`]: newPricePreTax ? `${roundUpNumber(newPricePreTax, 2)}` : 0
    });
    setFieldsValue({
      [`${countryNameCode}-nudosPrice`]:
        newPricePreTax && exchangeRate ? `${roundUpNumber(newPricePreTax * +exchangeRate, 2)}` : 0
    });
    setFieldsValue({
      [`${countryNameCode}-margin`]: `${roundUpNumber(calculatedMargin, 4)}`
    });
  };

  const handleChangeProviderPrice = (providerPriceInput: string) => {
    setFieldsValue({
      [`${countryNameCode}-providerPrice`]: providerPriceInput || 0
    });
    handleChangeMargin(margin);
  };

  const filterBySearchStringCallback = (search: string, rawOptions: IProviderDetails[]) => {
    return rawOptions.filter(taxData => taxData?.name?.toLowerCase().trim().includes(search.toLowerCase().trim()));
  };

  const formTaxDataDropdownOptionsFn = (
    setCurrentOption: React.Dispatch<React.SetStateAction<IProviderDetails | undefined>>
  ) => {
    return (rawOptions: IProviderDetails[], clickOptionCallback: (option: IProviderDetails) => void) => {
      return rawOptions.map((option, i) => {
        const handleSelectThisOption = () => {
          setFieldsValue({
            [`${countryNameCode}-providerId`]: option.id || null
          });
          setCurrentOption(option);
          clickOptionCallback && clickOptionCallback(option);
        };
        return (
          <div className="optionContainer" key={`option-${option.id}-${i}`} onClick={handleSelectThisOption}>
            {option.name}
          </div>
        );
      });
    };
  };

  const defaultProvider = () => {
    if (providerId && availableProviders && availableProviders.length > 0) {
      const initialProvider = availableProviders.find(element => element.id === providerId);
      if (initialProvider) {
        setNewProvider(initialProvider);
        setFieldsValue({
          [`${countryNameCode}-providerId`]: initialProvider?.id || null
        });
      } else {
        setFieldsValue({
          [`${countryNameCode}-providerId`]: null
        });
      }
    } else {
      setFieldsValue({
        [`${countryNameCode}-providerId`]: null
      });
    }
  };

  const setStatusProductToQuote = (pricingisActive?: boolean, isQuoteProduct?: boolean) => {
    if (!isOnlyDevsAutomates) return;
    if (isQuoteProduct) {
      setProductStatus(PRODUCT_STATUS.TO_QUOTE);
    } else {
      setProductStatus(pricingisActive ? PRODUCT_STATUS.ON : PRODUCT_STATUS.OFF);
    }
  };

  useEffect(() => {
    const pricingValuesAreValid = isMarginValid && isProviderPriceValid && isNudosPriceUsdWithTaxesInputValid;
    const hasRequiredFieldsByActiveStatus = activeCheck
      ? !!margin && !!providerPrice && !!nudosPriceUsdWithTaxes && pricingValuesAreValid
      : true;
    const isVerified =
      productStatus === PRODUCT_STATUS.TO_QUOTE ||
      (hasRequiredFieldsByActiveStatus &&
        pricingValuesAreValid &&
        typeof taxRate === 'number' &&
        typeof exchangeRate === 'number');
    setVerifyPricing && setVerifyPricing(isVerified);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    margin,
    providerPrice,
    taxRate,
    exchangeRate,
    activeCheck,
    setVerifyPricing,
    preloadedNudosPriceUsdWithTaxes,
    nudosProviderId
  ]);

  useEffect(() => {
    if (isOnlyDevsAutomates && !id) return;
    const hasChanged =
      margin !== preloadedMargin ||
      preloadedExchangeRate !== exchangeRate ||
      !!pricingisActive !== activeCheck ||
      preloadedProviderPrice !== providerPrice ||
      preloadedTaxRate !== taxRate ||
      preloadedNudosPriceUsdWithTaxes !== nudosPriceUsdWithTaxes ||
      nudosProviderId !== providerId;
    setChangePricing && setChangePricing(hasChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    margin,
    providerPrice,
    taxRate,
    exchangeRate,
    activeCheck,
    setChangePricing,
    nudosPriceUsdWithTaxes,
    nudosProviderId
  ]);

  useEffect(() => {
    if (productHasFullPricingDataForCountry) return;
    if (isOnlyDevsAutomates && productStatus === PRODUCT_STATUS.TO_QUOTE) return;
    setActiveCheck(false);
    setProductStatus(PRODUCT_STATUS.OFF);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productHasFullPricingDataForCountry, productStatus]);

  useEffect(() => {
    if (productHasFullPricingDataForCountry) setHighlightMissingFields(false);
  }, [productHasFullPricingDataForCountry]);

  useEffect(() => {
    const isActive = pricingisActive ? true : false;
    setActiveCheck(isActive);
    setStatusProductToQuote(pricingisActive, isQuoteProduct);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingisActive, isQuoteProduct]);

  useEffect(() => {
    if (isOnlyDevsAutomates) {
      const currentIsActive = pricingisActive ? true : false;
      const currentIsQuoteProduct = isQuoteProduct ? true : false;
      setFieldsValue({
        [`${countryNameCode}-isActive`]: currentIsActive,
        [`${countryNameCode}-isQuoteProduct`]: currentIsQuoteProduct
      });
    } else {
      setFieldsValue({
        [`${countryNameCode}-isActive`]: activeCheck
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryNameCode, isOnlyDevsAutomates, pricingisActive, isQuoteProduct, activeCheck]);

  useEffect(() => {
    setFieldsValue({
      [`${countryNameCode}-id`]: id,
      [`${countryNameCode}-name`]: countryName,
      [`${countryNameCode}-flag`]: flag,
      [`${countryNameCode}-inStock`]: preloadedStatus === 'Sin stock' ? 0 : 1
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, countryName, flag, preloadedStatus]);

  useEffect(() => {
    defaultProvider();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  useEffect(() => {
    setFieldsValue({
      [`${countryNameCode}-providerPrice`]: preloadedProviderPrice,
      [`${countryNameCode}-margin`]: preloadedMargin,
      [`${countryNameCode}-nudosPriceUsdWithTaxes`]: preloadedNudosPriceUsdWithTaxes,
      [`${countryNameCode}-nudosPriceUsd`]: preloadedNudosPriceUsd,
      [`${countryNameCode}-nudosPrice`]: 0,
      [`${countryNameCode}-nudosPriceWithTaxes`]: 0
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="pricingDataByCountry">
      {showPricingModalConfirm && pendingNewStatus !== null && (
        <BordModalConfirm
          onAccept={() => handleConfirmStatusChange()}
          onCancel={handleCancelStatusChange}
          title={`¿Estás seguro de cambiar el estado en ${countryName} de '${previousStatus}' a '${pendingNewStatus}'? ${handleStatusChangeMessage(
            pendingNewStatus
          )}`}
          textButtonAccept={handleLabelConfirmButton(pendingNewStatus)}
          textButtonCancel="No, cancelar"
          isLoading={isUpdatingStatus}
          isDisabled={isUpdatingStatus}
          customSizeIcon={48}
        />
      )}
      {!!blockedByQuotesError && pendingNewStatus !== null && (
        <UpdateBlockedByQuotesModal
          errorData={blockedByQuotesError.errorData}
          countryNames={blockedByQuotesError.countryNames}
          actionVerb={handleForceActionVerb(pendingNewStatus)}
          forceButtonText={handleForceButtonLabel(pendingNewStatus)}
          closeModal={handleCloseBlockedByQuotes}
          onForce={handleForceStatusChange}
          isForcing={isUpdatingStatus}
        />
      )}
      <div className="countryInfo">
        <div className="countryFlag" style={{ backgroundImage: `url(${flag})` }} />
        <div className="countryName">{countryName}</div>
      </div>
      <NudosMaskedNumberInput
        componentSize="small"
        label={`Costo de compra (${currencyCode})`}
        placeholder={`$0 ${currencyCode}`}
        currentValue={providerPrice}
        handleChange={inputText => handleChangeProviderPrice(inputText || '')}
        maskPrefix={'$'}
        maskSufix={` ${currencyCode}`}
        roundToClosest
        numberOfDecimals={2}
        errorText={
          (!isProviderPriceValid && productStatus !== PRODUCT_STATUS.TO_QUOTE) || (highlightMissingFields && !providerPrice)
            ? ' '
            : undefined
        }
      />
      <NudosMaskedNumberInput
        label={`Margen`}
        componentSize="extraSmall"
        placeholder={'0 %'}
        currentValue={`${margin || ''}`}
        handleChange={inputText => handleChangeMargin(inputText || '')}
        maskPrefix={''}
        maskSufix={'%'}
        roundToClosest
        numberOfDecimals={4}
        errorText={negativeOrZeroMarginAlert || requiredFieldError(!margin)}
        inputPadding={12}
      />
      {pricingFields.map(field => {
        return (
          <PricingInputField
            key={field.dataName}
            componentSize={field.componentSize}
            dataName={field.dataName}
            label={field.label}
            initialValue={typeof field.initialValue === 'number' ? field.initialValue : 0}
            currentValue={typeof field.currentValue === 'number' ? field.currentValue : 0}
            suffixSymbol={field.suffixSymbol}
            placeholder={field.placeholder}
            isRequired={field.isRequired}
            isDisabled={field.isDisabled}
            hasError={highlightMissingFields && !field.currentValue}
          />
        );
      })}
      <NudosMaskedNumberInput
        componentSize="small"
        label={getEcommercePriceSelectLabel(false)}
        placeholder={emptyFieldPlaceholder}
        isDeactivated={true}
        currentValue={nudosPriceUsd}
        maskPrefix={'$'}
        maskSufix={' USD'}
        roundToClosest
        numberOfDecimals={2}
      />
      <NudosMaskedNumberInput
        componentSize="small"
        label={getEcommercePriceSelectLabel(true)}
        placeholder={emptyFieldPlaceholder}
        isDeactivated={false}
        handleChange={inputText => handleChangeNudosPriceUsdWithTaxes(inputText || '')}
        currentValue={nudosPriceUsdWithTaxes}
        maskPrefix={'$'}
        maskSufix={' USD'}
        roundToClosest
        numberOfDecimals={2}
        errorText={
          !isNudosPriceUsdWithTaxesInputValid || (highlightMissingFields && !nudosPriceUsdWithTaxes) ? ' ' : undefined
        }
      />
      <div className="containerSearcher">
        <NudosDropdownWithSearchbar
          label="Proveedor"
          componentSize="mediumSmall"
          customClassLabel="customClassLabel"
          formDropdownOptionsCallback={formTaxDataDropdownOptionsFn(setNewProvider)}
          filterBySearchStringCallback={filterBySearchStringCallback}
          customPlaceholder="Proveedor"
          currentValue={newProvider}
          isFilled={!!newProvider}
          rawOptions={availableProviders || []}
        />
      </div>
      <div className={`productStatusSwitch ${isOnlyDevsAutomates ? 'withoutPadding' : ''}`}>
        {preloadedStatus === 'Sin stock' ? (
          <StatusButton status="outOfStockButton" />
        ) : isOnlyDevsAutomates ? (
          <div className="productStatusTabsContainer">
            <span className="productStatusTabsTitle">Estado de la herramienta en tienda</span>
            <ProductStatusTabs currentStatus={productStatus} onStatusChange={handleStatusChange} />
          </div>
        ) : (
          <div id="customSwitch">
            <Switch
              checked={productHasFullPricingDataForCountry ? activeCheck : false}
              onChange={handleClickSwitchActiveStatus}
              style={{ width: 'max-content', height: '24px' }}
              checkedChildren="Prendido"
              unCheckedChildren="Apagado"
            />
            <Popover
              trigger="click"
              placement="topRight"
              open={showActiveStatusPopover}
              content={confirmChangeActiveStatusPopoverContent}
            />
          </div>
        )}
        {!productHasFullPricingDataForCountry && (
          <div className="mustCompleteDataHover">Para marcar como Prendido, primero debes completar los campos</div>
        )}
        {productHasFullPricingDataForCountry && needsSaveBeforeTurnOn && (
          <div className="mustCompleteDataHover">Primero guarda los precios para poder prender el país en tienda</div>
        )}
      </div>
      <Form.Item name={`${countryNameCode}-isActive`} hidden />
      <Form.Item name={`${countryNameCode}-isQuoteProduct`} hidden />
      <Form.Item name={`${countryNameCode}-id`} hidden />
      <Form.Item name={`${countryNameCode}-name`} hidden />
      <Form.Item name={`${countryNameCode}-flag`} hidden />
      <Form.Item name={`${countryNameCode}-inStock`} hidden />
      <Form.Item name={`${countryNameCode}-providerPrice`} hidden />
      <Form.Item name={`${countryNameCode}-margin`} hidden />
      <Form.Item name={`${countryNameCode}-nudosPriceUsd`} hidden />
      <Form.Item name={`${countryNameCode}-nudosPrice`} hidden />
      <Form.Item name={`${countryNameCode}-nudosPriceUsdWithTaxes`} hidden />
      <Form.Item name={`${countryNameCode}-nudosPriceWithTaxes`} hidden />
      <Form.Item name={`${countryNameCode}-providerId`} hidden />
    </div>
  );
};

export default PricingDataByCountry;
